import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, LineReveal } from "@/components/site/motion";
import { useFormspree } from "@/lib/formspree";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SIZES = ["Under 100", "100 to 250", "250 to 500", "500 to 1,000", "Over 1,000"];
const HOSTING = [
  { v: "Hosted for us", d: "Grace Treasury is hosted for your church, for a fee." },
  { v: "Self-hosted", d: "You run it on your own system." },
  { v: "Self-hosted with installation help", d: "The developer installs it for you, on-site or remotely, for a fee." },
];
const EMPTY = { name: "", church_name: "", role: "", email: "", phone: "", congregation_size: "", hosting_preference: "", message: "" };

const Field = ({ label, id, optional, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-700">
      {label} {optional && <span className="normal-case tracking-normal text-slate-500">(optional)</span>}
    </span>
    {children}
  </label>
);

const HostingChoice = ({ value, onChange }) => (
  <fieldset className="sm:col-span-2" data-testid="demo-hosting-choice">
    <legend className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-700">
      Hosting preference <span className="normal-case tracking-normal text-slate-500">(optional)</span>
    </legend>
    <div className="grid gap-3 md:grid-cols-3">
      {HOSTING.map((h) => {
        const on = value === h.v;
        return (
          <label key={h.v} className={`flex cursor-pointer items-start gap-3 border p-4 transition-colors duration-200 ${on ? "border-navy bg-[#faf4e8]" : "border-slate-300 hover:border-[#c5a059]"}`} data-testid={`demo-hosting-option-${h.v.toLowerCase().replace(/ /g, "-")}`}>
            <input type="radio" name="hosting_preference" value={h.v} checked={on} onChange={() => onChange(h.v)} className="mt-1 accent-[#071330]" />
            <span>
              <span className="block text-sm font-semibold text-navy">{h.v}</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-600">{h.d}</span>
            </span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

const DemoForm = () => {
  const { status, error, submit, reset, turnstileRef } = useFormspree("demo");
  const [v, setV] = useState(EMPTY);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  if (status === "success") {
    return (
      <div className="flex flex-col items-start gap-5 py-6" data-testid="demo-form-success">
        <CheckCircle2 className="h-10 w-10 text-[#8a6a2f]" />
        <h2 className="font-display text-4xl font-semibold text-navy">Thank you, {v.name.split(" ")[0]}.</h2>
        <p className="max-w-md text-base leading-relaxed text-slate-600">Your demo request for {v.church_name} has been received. We'll reply to {v.email} to arrange a time.</p>
        <button type="button" className="btn-outline" onClick={() => { setV(EMPTY); reset(); }} data-testid="demo-form-reset-button">Send another request</button>
      </div>
    );
  }

  const onSubmit = (e) => {
    e.preventDefault();
    submit({ ...v, _subject: `Demo request [${v.hosting_preference || "Hosting: not specified"}]: ${v.church_name}`, _replyto: v.email });
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-6 sm:grid-cols-2" data-testid="demo-form">
      <Field label="Your name" id="name"><input id="name" required value={v.name} onChange={set("name")} className="field" data-testid="demo-name-input" /></Field>
      <Field label="Church name" id="church"><input id="church" required value={v.church_name} onChange={set("church_name")} className="field" data-testid="demo-church-input" /></Field>
      <Field label="Your role" id="role"><input id="role" required value={v.role} onChange={set("role")} placeholder="Treasurer, pastor, bookkeeper…" className="field" data-testid="demo-role-input" /></Field>
      <Field label="Email" id="email"><input id="email" type="email" required value={v.email} onChange={set("email")} className="field" data-testid="demo-email-input" /></Field>
      <Field label="Phone" id="phone" optional><input id="phone" type="tel" value={v.phone} onChange={set("phone")} className="field" data-testid="demo-phone-input" /></Field>
      <Field label="Congregation size" id="size">
        <Select value={v.congregation_size} onValueChange={(val) => setV({ ...v, congregation_size: val })} required>
          <SelectTrigger id="size" className="field h-12 rounded-none" data-testid="demo-size-select"><SelectValue placeholder="Select a size" /></SelectTrigger>
          <SelectContent data-lenis-prevent>
            {SIZES.map((s) => <SelectItem key={s} value={s} data-testid={`demo-size-option-${s.replace(/[^0-9a-z]+/gi, "-").toLowerCase()}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <HostingChoice value={v.hosting_preference} onChange={(val) => setV({ ...v, hosting_preference: val })} />
      <div className="sm:col-span-2">
        <Field label="Message" id="message"><textarea id="message" required rows={5} value={v.message} onChange={set("message")} placeholder="What would you like to see?" className="field h-auto py-3" data-testid="demo-message-input" /></Field>
      </div>
      <div ref={turnstileRef} className="empty:hidden sm:col-span-2" data-testid="demo-turnstile" />
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center">
        <button type="submit" disabled={status === "sending"} className="btn-primary sm:w-auto" data-testid="demo-submit-button">
          {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Request a demo"}
        </button>
        {error && <p role="alert" className="text-sm text-red-700" data-testid="demo-form-error">{error}</p>}
      </div>
    </form>
  );
};

export default function Demo() {
  return (
    <div className="bg-parchment" data-testid="demo-page">
      <div className="container-x grid gap-12 py-16 sm:py-24 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <p className="eyebrow">Demo</p>
          <LineReveal lines={["Request", "a demo."]} className="mt-6 font-display text-5xl font-semibold leading-[1.04] tracking-tight text-navy sm:text-6xl" />
          <Reveal delay={0.3}>
            <p className="mt-8 text-base leading-relaxed text-slate-600">Tell us a little about your church and what you'd like to see. Your request goes straight to the Grace Treasury team.</p>
            <div className="mt-10 space-y-2 border-t border-slate-300 pt-6 text-sm text-slate-600" data-testid="contact-details">
              <p className="eyebrow mb-3">Write to us</p>
              <p><a href="mailto:info@gracetreasury.app" className="text-navy underline underline-offset-4 transition-colors hover:text-[#8a6a2f]" data-testid="demo-email-link">info@gracetreasury.app</a></p>
              <p className="leading-relaxed" data-testid="demo-address">Grace Treasury<br />1904 Johns Creek Rd.<br />Milton, WV 25541</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="border border-[#c5a059]/45 bg-white p-6 sm:p-10 lg:p-12">
            <DemoForm />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
